import React from 'react';
import PropTypes from 'prop-types';
import { render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';
import { NotificationsPanel, useNotifications } from '@decisiv/ui-components';

import AppConfigProvider from './ConfigProvider';
import Toasts, { TOAST_Z_INDEX } from './Toasts';

function Page({ panel }) {
  const { notify } = useNotifications();

  return (
    <>
      <button
        type="button"
        onClick={() =>
          // onClose is what makes KeyDS render the close "X", and its hint.
          notify({ title: 'Permissions updated', onClose: () => {} })
        }
      >
        Save
      </button>
      {panel}
    </>
  );
}

Page.propTypes = {
  panel: PropTypes.node.isRequired,
};

// Raise a toast, hover its close button, and hand back the hint's own portaled
// container -- the element the z-index actually lands on.
async function hoverCloseHint(panel) {
  render(
    <AppConfigProvider>
      <Page panel={panel} />
    </AppConfigProvider>,
  );

  userEvent.click(screen.getByRole('button', { name: 'Save' }));
  await screen.findByText('Permissions updated');

  userEvent.hover(screen.getByLabelText(/close/i));

  const hint = await screen.findByText('Close');
  await waitFor(() =>
    expect(document.getElementById('key-popovers')).toContainElement(hint),
  );

  return { hint, content: hint.closest('[class*="PopoverContent"]') };
}

const zIndexOf = (element) => Number(window.getComputedStyle(element).zIndex);

describe('Toasts', () => {
  it('renders the toast panel', () => {
    render(
      <AppConfigProvider>
        <Toasts />
      </AppConfigProvider>,
    );

    expect(document.getElementById('key-popovers')).toBeInTheDocument();
  });

  // PD-4722. KeyDS gives a Popover `currentZIndex + zIndex`, and Notification
  // hardcodes its close hint to 100, so without a stacking context around the
  // panel the hint computes 0 + 100 and paints behind the panel's own 1000.
  describe("the close button's hint", () => {
    it('renders above the toast panel', async () => {
      const { content } = await hoverCloseHint(<Toasts />);

      expect(zIndexOf(content)).toBeGreaterThan(TOAST_Z_INDEX);
    });

    // The bug this replaced, kept as the control: the same panel without the
    // stacking context puts the hint under the toast it belongs to. Guards the
    // fix against being removed as a no-op.
    it('renders below the panel without the stacking context', async () => {
      const { content } = await hoverCloseHint(
        <NotificationsPanel marginTop={4} zIndex={TOAST_Z_INDEX} />,
      );

      expect(zIndexOf(content)).toBeLessThan(TOAST_Z_INDEX);
    });

    // The z-index above is only the right one if it is measured on the element
    // that carries the hint. If KeyDS ever restructures Notification's hint,
    // this fails rather than letting the assertion drift onto another overlay.
    it('is the element the raised z-index is measured on', async () => {
      const { content, hint } = await hoverCloseHint(<Toasts />);

      expect(content).toContainElement(hint);
    });
  });

  it('renders no DOM of its own around the panel', async () => {
    const { container: wrapped } = render(
      <AppConfigProvider>
        <Toasts />
      </AppConfigProvider>,
    );
    const { container: bare } = render(
      <AppConfigProvider>
        <NotificationsPanel marginTop={4} zIndex={TOAST_Z_INDEX} />
      </AppConfigProvider>,
    );

    expect(wrapped.innerHTML).toEqual(bare.innerHTML);
  });
});
